"use client";

// imports
import Cookies from 'js-cookie';
import Link from 'next/link';
import { useState } from "react";

// lib files
import { PromoBadgeColors } from '@/lib/types/promoBadges';
import { sendPbsGtmEvent } from "@/lib/helpers/send-pbs-gtm-event";
import { StationData } from '@/lib/types/api/stations-data';
import { useHasMounted } from '@/lib/hooks';
import { useIdentity } from '@/lib/hooks/useIdentity';

// constants
import { HIDE_PROMO_BADGE_COOKIE, HIDE_PROMO_BADGE_COOKIE_AGE_DAYS } from '@/lib/constants';

// components
import ITSImage from '@/components/ITSImage/ITSImage';
import PassportLockup from "@/components/PassportLearnMore/PassportLockup/PassportLockup";

// svgs
import NextIcon from '@/public/svg/caret-next.svg';
import CloseIcon from '@/public/svg/close.svg';

// styles
import styles from './PromoBadge.module.scss';

interface MarbleHallMurdersPromoBadgeProps {
  stationData: StationData;
  depIsPassportMember?: boolean;
}

// Badge configuration constants
// we used to pass these as props, but it was too hard to make this component reusable from one
// promo to the next. this was just simpler. the idea is that we can duplicate these badge files
// and change these constants for each promo we want to run, and prune what we don't use.
const CAMPAIGN_TAG = 'marblemurders_2026';
const COLOR: PromoBadgeColors = 'marble_hall_brown';
const CTA_TEXT = 'Donate and Start Watching';
const CTA_URL = '/donate/';
const DISPLAY_PASSPORT_LOCKUP = true;
const HEADLINE = 'Stream the new season with';
const PASSPORT_MEMBER_CTA_TEXT = 'Watch Now';
const PASSPORT_MEMBER_CTA_URL = '/show/marble-hall-murders/';
const PASSPORT_MEMBER_HEADLINE = 'Stream all episodes with';
const SHOW_LOGO_ALT = 'Masterpiece: Marble Hall Murders';
const SHOW_LOGO_URL = 'https://image.pbs.org/webobjects/HorizontalMarbleHallMurders-RhZkov4.png';
const SHOW_LOGO_URL_MOBILE = 'https://image.pbs.org/webobjects/VerticalMarbleHallMurders-4jYO5yt.png';
const USE_PASSPORT_URL_FOR_CTA_URL = true;

// UTM campaign tags
const UTM_SOURCE = "pbs_org";
const UTM_MEDIUM = "homepage_badge";

const MarbleHallMurdersPromoBadge = (props: MarbleHallMurdersPromoBadgeProps) => {
    const { isSignedIn, cachedIdentity } = useIdentity()
    const [hidden, setHidden] = useState(() => Cookies.get(HIDE_PROMO_BADGE_COOKIE) === 'true');
    const hasMounted = useHasMounted();
    const {
      stationData,
      depIsPassportMember,
    } = props;
    const isPassportMember =
      // did we pass the depIsPassportMember prop (e.g. from storybook or tests)
      depIsPassportMember !== undefined ? depIsPassportMember :
      // are they is_passport. Cached is fine: this only picks badge copy, and
      // `undefined` (not yet known) intentionally reads as non-member here.
      cachedIdentity?.isPassport || false;

    const hideBadge = () => {
      Cookies.set(
        HIDE_PROMO_BADGE_COOKIE,
        'true',
        {
          path: '/',
          expires: HIDE_PROMO_BADGE_COOKIE_AGE_DAYS
        });
      setHidden(true);
      sendPbsGtmEvent({
        feature_category: 'homepage badge',
        feature_name: "homepage badge dismiss",
        object_action: "click",
        object_action_behavior: 'user dismisses badge',
        object_name: "homepage badge close button",
        object_type: "button",
        signed_in_status: isSignedIn,
      })
    };

    const classes = styles.promo_badge;

    let ctaText = CTA_TEXT;
    let headline = HEADLINE;
    let ctaUrl = CTA_URL;

    // if the flag is set to use the station passport url for cta url,
    // we get the passport url from station data
    if (USE_PASSPORT_URL_FOR_CTA_URL) {
      ctaUrl = stationData?.attributes.passport_url || CTA_URL;
    }

    // if the user is a PassportMember
    if (isPassportMember) {
      // use Passport-specific CTA & headlines
      ctaText = PASSPORT_MEMBER_CTA_TEXT;
      headline = PASSPORT_MEMBER_HEADLINE;
      // the passport CTA url is still optional, so only change that if we have it
      if (PASSPORT_MEMBER_CTA_URL) {
        ctaUrl = PASSPORT_MEMBER_CTA_URL;
      }
    }

    // Adding utm tags to ctaUrl, whatever it is
    const utmTagParams = new URLSearchParams();

    utmTagParams.append('utm_source', UTM_SOURCE);
    utmTagParams.append('utm_medium', UTM_MEDIUM);

    if (CAMPAIGN_TAG) {
      utmTagParams.append('utm_campaign', CAMPAIGN_TAG);
    }

    // RWEB-9735 If the ctaUrl already has query params, we need to use '&' instead of '?'
    const tagParamJoiner = ctaUrl.includes('?') ? '&' : '?';

    ctaUrl = `${ctaUrl}${tagParamJoiner}${utmTagParams.toString()}`;

    const shortCommonName = stationData?.attributes.short_common_name || 'PBS';

    // Checks if component is mounted OR if the badge is hidden
    // on the client before attempting to hydrate, otherwise a hydration error occurs
    if (!hasMounted || hidden) {
      return null;
    }

    const handleCTAClick = () => {
      const behavior = isPassportMember ?
        "send user to watch content" :
        "send user to badge cta link to donate";

      sendPbsGtmEvent({
        feature_category: 'homepage badge',
        feature_name: "homepage badge cta",
        object_action: "click",
        object_action_behavior: behavior,
        object_name: "homepage badge cta link",
        object_text: ctaText,
        object_type: "link",
        object_url: ctaUrl,
        signed_in_status: isSignedIn,
      })
    }

    const handleLogoClick = () => {
      const behavior = isPassportMember ?
        "send user to watch content" :
        "send user to badge cta link to donate";

      sendPbsGtmEvent({
        feature_category: 'homepage badge',
        feature_name: "homepage badge logo",
        object_action: "click",
        object_action_behavior: behavior,
        object_name: "homepage badge logo link",
        object_type: "link",
        object_url: ctaUrl,
        signed_in_status: isSignedIn,
      })
    }

    return (
      <div className={`${styles.promo_badge__wrapper} ${styles[COLOR]}`}>
        <div className={`${classes} ${styles[COLOR]}`}>
          <button
            className={styles.promo_badge__close_button}
            onClick={hideBadge}
            aria-label='Close'
          >
            <CloseIcon />
          </button>
          <Link
            className={styles.promo_badge__show_logo}
            href={ctaUrl}
            onClick={handleLogoClick}
            >
            <ITSImage
              src={SHOW_LOGO_URL}
              alt={SHOW_LOGO_ALT}
              width={400}
              className={styles.promo_badge__show_logo_desktop}
            />
            <ITSImage
              src={SHOW_LOGO_URL_MOBILE}
              alt={SHOW_LOGO_ALT}
              width={400}
              className={styles.promo_badge__show_logo_mobile}
            />
          </Link>
          <span className={styles.promo_badge__headline}>
            {headline}
            { DISPLAY_PASSPORT_LOCKUP && ctaText.length > 0 && headline.length > 0 && (
              <PassportLockup
                shortCommonName={shortCommonName}
                element={"h6"}
                className={styles.passport_lockup}
              />
            )}
          </span>
          <Link
            className={styles.promo_badge__cta}
            href={ctaUrl}
            title={ctaText}
            onClick={handleCTAClick}
          >
            {ctaText}
            <NextIcon className={styles.next_icon} />
          </Link>
        </div>
      </div>
    );
};

export default MarbleHallMurdersPromoBadge;
